import { useEffect, useRef, useState } from 'react';

/**
 * Tracks whether a `position: sticky` element is currently pinned to the top of
 * its scroll container, so it can restyle itself while pinned.
 *
 * Not `@container scroll-state(stuck: top)`: that only re-evaluates on scroll,
 * so an already-scrolled page keeps rendering the un-stuck styles after a style
 * change (it silently read as "sticky does nothing"). This measures on mount too.
 */
export default function useStuckAtTop(scrollRootSelector = '.main-content') {
  const ref = useRef<HTMLDivElement | null>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const root = el.closest(scrollRootSelector) as HTMLElement | null;
    const scroller: HTMLElement | Window = root ?? window;

    let frame = 0;
    const measure = () => {
      frame = 0;
      // Pinned ⇔ the element sits at the very top of the scroll container. Both
      // the sticky offset and the container's padding derive from --main-pad-top,
      // so the pinned position is the container's own top edge.
      const rootTop = root ? root.getBoundingClientRect().top : 0;
      setStuck(el.getBoundingClientRect().top <= rootTop + 0.5);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(measure); };

    measure();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [scrollRootSelector]);

  return { ref, stuck };
}
